/**
 * @fileoverview Field panel for the custom-export pivot builder.
 *
 * Purpose: the same spreadsheet-style compose UI as the timesheets pivot
 * builder (`PivotFieldPanel`) — a searchable field list on the left and
 * Filters / Columns / Rows / Values drop zones on the right — over a
 * `CustomExportSpec`. Every dataset header is both a grouping dimension
 * and a measure (with an aggregation chosen on the values chip).
 *
 * Caps match the engine: two row levels, one column, any number of values.
 * Filters are the spec's simple AND-of-conditions shape (no OR / brackets).
 *
 * @component
 * @example
 * <CustomExportFieldPanel dataset={dataset} spec={spec} onChange={patch} />
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Check,
  Columns3,
  Filter,
  GripVertical,
  Rows3,
  Sigma,
  X,
} from "lucide-react";

import type {
  CustomExportFilter,
  CustomExportMeasure,
  CustomExportSpec,
} from "@cvx/savedCustomExports/schema";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  AGG_LABELS,
  distinctValues,
  guessAgg,
  type CustomExportDataset,
} from "./engine";

const ROW_CAP = 2;
const COLUMN_CAP = 1;

const AGG_KEYS = Object.keys(AGG_LABELS) as CustomExportMeasure["agg"][];

type ZoneId = "filters" | "columns" | "rows" | "values";

/** What a drag is carrying, and where it came from if it started in a zone. */
interface DragData {
  kind: "dimension" | "measure";
  key: string;
  from?: ZoneId;
  index?: number;
}

const CHIP =
  "inline-flex max-w-full items-center gap-1 rounded-md border border-border/60 bg-background py-1 pl-1.5 pr-0.5 text-xs";

const DRAG_HANDLE = "touch-none select-none cursor-grab active:cursor-grabbing";

/**
 * Prefer the zone the pointer is actually over. `closestCenter` compares
 * droppable centres, so an empty Filters box loses to Columns beside it
 * and the drop looks like a no-op.
 */
const zoneCollision: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  return rectIntersection(args);
};

/** Wait out the drop's pointer-up so it cannot dismiss the value picker. */
const PICKER_ARM_MS = 120;

function insertCapped<T extends string>(
  list: readonly T[],
  key: T,
  at: number | undefined,
  cap: number,
): T[] {
  const next = list.filter((item) => item !== key);
  next.splice(Math.min(at ?? next.length, next.length), 0, key);
  if (next.length > cap) {
    for (let i = next.length - 1; i >= 0; i -= 1) {
      if (next[i] !== key) {
        next.splice(i, 1);
        break;
      }
    }
  }
  return next;
}

function insertMeasure(
  list: readonly CustomExportMeasure[],
  measure: CustomExportMeasure,
  at: number | undefined,
): CustomExportMeasure[] {
  const next = list.filter((item) => item.column !== measure.column);
  next.splice(Math.min(at ?? next.length, next.length), 0, measure);
  return next;
}

export function CustomExportFieldPanel({
  dataset,
  spec,
  onChange,
}: {
  dataset: CustomExportDataset;
  spec: CustomExportSpec;
  onChange: (patch: Partial<CustomExportSpec>) => void;
}) {
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState<DragData | null>(null);
  /** The condition being composed; `index` is null for a new one. */
  const [editing, setEditing] = useState<{
    column: string;
    index: number | null;
    values: string[];
  } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (armTimer.current) clearTimeout(armTimer.current);
    },
    [],
  );

  const startNewFilter = (column: string) => {
    setPickerOpen(false);
    setEditing({ column, index: null, values: [] });
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = setTimeout(() => setPickerOpen(true), PICKER_ARM_MS);
  };

  const closeFilter = () => {
    setPickerOpen(false);
    setEditing(null);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    }),
  );

  const pivotColumns = spec.pivotColumn ? [spec.pivotColumn] : [];
  const measureColumns = spec.measures.map((m) => m.column);

  const optionsByColumn = useMemo(() => {
    const map: Record<string, Array<{ value: string; label: string }>> = {};
    for (const header of dataset.headers) {
      map[header] = distinctValues(dataset, header).map((value) => ({
        value,
        label: value || "(blank)",
      }));
    }
    return map;
  }, [dataset]);

  const place = (zone: ZoneId, data: DragData, at?: number) => {
    if (zone === "filters") {
      startNewFilter(data.key);
      return;
    }
    if (zone === "values") {
      if (data.kind !== "measure") return;
      const existing = spec.measures.find((m) => m.column === data.key);
      onChange({
        measures: insertMeasure(
          spec.measures,
          existing ?? { column: data.key, agg: guessAgg(dataset, data.key) },
          at,
        ),
      });
      return;
    }
    if (data.kind !== "dimension") return;

    if (zone === "rows") {
      const rows = insertCapped(spec.rows, data.key, at, ROW_CAP);
      onChange({
        rows,
        pivotColumn:
          spec.pivotColumn === data.key ? undefined : spec.pivotColumn,
      });
      return;
    }

    if (spec.rows.length === 1 && spec.rows[0] === data.key) return;
    const rows = spec.rows.filter((r) => r !== data.key);
    const columns = insertCapped(pivotColumns, data.key, at, COLUMN_CAP);
    onChange({
      rows,
      pivotColumn: columns[0],
    });
  };

  const remove = (zone: ZoneId, index: number) => {
    if (zone === "rows") {
      onChange({ rows: spec.rows.filter((_, i) => i !== index) });
      return;
    }
    if (zone === "columns") {
      onChange({ pivotColumn: undefined });
      return;
    }
    if (zone === "values") {
      onChange({
        measures: spec.measures.filter((_, i) => i !== index),
      });
      return;
    }
    onChange({ filters: spec.filters.filter((_, i) => i !== index) });
  };

  const toggleDimension = (header: string) => {
    if (spec.rows.includes(header)) {
      remove("rows", spec.rows.indexOf(header));
      return;
    }
    if (spec.pivotColumn === header) {
      remove("columns", 0);
      return;
    }
    place(spec.rows.length < ROW_CAP ? "rows" : "columns", {
      kind: "dimension",
      key: header,
    });
  };

  const toggleMeasure = (header: string) => {
    const index = spec.measures.findIndex((m) => m.column === header);
    if (index === -1) {
      place("values", { kind: "measure", key: header });
      return;
    }
    remove("values", index);
  };

  const setMeasureAgg = (column: string, agg: CustomExportMeasure["agg"]) => {
    onChange({
      measures: spec.measures.map((m) =>
        m.column === column ? { ...m, agg } : m,
      ),
    });
  };

  const commitCondition = (negate: boolean) => {
    if (!editing || editing.values.length === 0) return;
    const condition: CustomExportFilter = {
      column: editing.column,
      negate,
      values: editing.values,
    };
    if (editing.index === null) {
      onChange({ filters: [...spec.filters, condition] });
    } else {
      onChange({
        filters: spec.filters.map((f, i) =>
          i === editing.index ? condition : f,
        ),
      });
    }
    closeFilter();
  };

  const matches = (label: string) =>
    label.toLowerCase().includes(search.trim().toLowerCase());

  const handleDragStart = (event: DragStartEvent) =>
    setDragging(event.active.data.current as DragData);

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const data = event.active.data.current as DragData | undefined;
    const target = event.over?.data.current as
      { zone: ZoneId; index?: number } | undefined;
    if (!data || !target) return;
    place(target.zone, data, target.index);
  };

  const accepts = (zone: ZoneId): boolean => {
    if (!dragging) return false;
    if (zone === "filters") return true;
    if (zone === "values") return dragging.kind === "measure";
    return dragging.kind === "dimension";
  };

  const draggedLabel = dragging ? dragging.key : "";

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={zoneCollision}
      autoScroll={false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      <div className="grid gap-3 lg:grid-cols-[220px_1fr]">
        <div className="flex flex-col gap-2 rounded-lg border border-border/70 p-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fields…"
            className="h-7 text-xs"
          />
          <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
            <FieldGroup label="Group by" />
            {dataset.headers.filter(matches).map((header) => (
              <FieldItem
                key={`dimension:${header}`}
                data={{ kind: "dimension", key: header }}
                label={header}
                checked={
                  spec.rows.includes(header) || spec.pivotColumn === header
                }
                onToggle={() => toggleDimension(header)}
              />
            ))}
            <FieldGroup label="Measure" />
            {dataset.headers.filter(matches).map((header) => (
              <FieldItem
                key={`measure:${header}`}
                data={{ kind: "measure", key: header }}
                label={header}
                checked={measureColumns.includes(header)}
                onToggle={() => toggleMeasure(header)}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <Zone
            zone="filters"
            label="Filters"
            icon={Filter}
            hint="Keep only some rows — drag a field here, or use Add"
            accepts={accepts("filters")}
            dragging={!!dragging}
            empty={spec.filters.length === 0 && !editing}
            action={
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  >
                    Add
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {dataset.headers.map((header) => (
                    <DropdownMenuItem
                      key={header}
                      onClick={() => startNewFilter(header)}
                    >
                      {header}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          >
            {spec.filters.map((filter, index) => (
              <ConditionChip
                key={`${filter.column}:${index}`}
                column={filter.column}
                options={optionsByColumn[filter.column] ?? []}
                token={filter}
                open={pickerOpen && editing?.index === index}
                onOpenChange={(open) => {
                  if (open) {
                    setEditing({
                      column: filter.column,
                      index,
                      values: filter.values,
                    });
                    setPickerOpen(true);
                    return;
                  }
                  closeFilter();
                }}
                values={editing?.index === index ? editing.values : []}
                onValuesChange={(values) =>
                  setEditing((prev) => (prev ? { ...prev, values } : prev))
                }
                onApply={commitCondition}
                onRemove={() => remove("filters", index)}
              />
            ))}
            {editing?.index === null && (
              <ConditionChip
                column={editing.column}
                options={optionsByColumn[editing.column] ?? []}
                open={pickerOpen}
                onOpenChange={(open) => !open && closeFilter()}
                values={editing.values}
                onValuesChange={(values) =>
                  setEditing((prev) => (prev ? { ...prev, values } : prev))
                }
                onApply={commitCondition}
                onRemove={closeFilter}
              />
            )}
          </Zone>

          <Zone
            zone="columns"
            label="Columns"
            icon={Columns3}
            hint="One field across the top"
            accepts={accepts("columns")}
            dragging={!!dragging}
            empty={pivotColumns.length === 0}
          >
            {pivotColumns.map((header, index) => (
              <FieldChip
                key={header}
                zone="columns"
                index={index}
                data={{
                  kind: "dimension",
                  key: header,
                  from: "columns",
                  index,
                }}
                label={header}
                onRemove={() => remove("columns", index)}
              />
            ))}
          </Zone>

          <Zone
            zone="rows"
            label="Rows"
            icon={Rows3}
            hint="Up to two levels down the side"
            accepts={accepts("rows")}
            dragging={!!dragging}
            empty={spec.rows.length === 0}
          >
            {spec.rows.map((header, index) => (
              <FieldChip
                key={header}
                zone="rows"
                index={index}
                data={{
                  kind: "dimension",
                  key: header,
                  from: "rows",
                  index,
                }}
                label={header}
                note={index === 1 ? "then by" : undefined}
                onRemove={() => remove("rows", index)}
              />
            ))}
          </Zone>

          <Zone
            zone="values"
            label="Values"
            icon={Sigma}
            hint="What fills the cells"
            accepts={accepts("values")}
            dragging={!!dragging}
            empty={spec.measures.length === 0}
          >
            {spec.measures.map((measure, index) => (
              <FieldChip
                key={`${measure.agg}:${measure.column}`}
                zone="values"
                index={index}
                data={{
                  kind: "measure",
                  key: measure.column,
                  from: "values",
                  index,
                }}
                label={measure.column}
                agg={measure.agg}
                onAggChange={(agg) => setMeasureAgg(measure.column, agg)}
                onRemove={() => remove("values", index)}
              />
            ))}
          </Zone>
        </div>
      </div>

      <DragOverlay dropAnimation={null} style={{ zIndex: 200 }}>
        {dragging && (
          <span className={cn(CHIP, "pr-1.5 shadow-md")}>{draggedLabel}</span>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function FieldGroup({ label }: { label: string }) {
  return (
    <p className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
      {label}
    </p>
  );
}

function FieldItem({
  data,
  label,
  checked,
  onToggle,
}: {
  data: DragData;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `field:${data.kind}:${data.key}`,
    data,
  });

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-1 py-1 text-xs hover:bg-muted/60",
        isDragging && "opacity-40",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        aria-label={label}
      />
      <button
        ref={setNodeRef}
        type="button"
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1 text-left",
          DRAG_HANDLE,
        )}
        title="Drag into Filters, Columns, Rows or Values"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3 shrink-0 text-muted-foreground/50" />
        <span className="truncate">{label}</span>
      </button>
    </div>
  );
}

function Zone({
  zone,
  label,
  icon: Icon,
  hint,
  accepts,
  dragging,
  empty,
  action,
  children,
}: {
  zone: ZoneId;
  label: string;
  icon: typeof Filter;
  hint: string;
  accepts: boolean;
  dragging: boolean;
  empty: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `zone:${zone}`,
    data: { zone },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-[76px] flex-col gap-1.5 rounded-lg border border-dashed p-2 transition-colors",
        isOver && accepts
          ? "border-primary bg-primary/5"
          : "border-border/70 bg-muted/30",
        dragging && !accepts && "opacity-50",
      )}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="size-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-1">{children}</div>
      {empty && (
        <span className="text-xs italic text-muted-foreground/70">{hint}</span>
      )}
    </div>
  );
}

function FieldChip({
  zone,
  index,
  data,
  label,
  note,
  agg,
  onAggChange,
  onRemove,
}: {
  zone: ZoneId;
  index: number;
  data: DragData;
  label: string;
  note?: string;
  agg?: CustomExportMeasure["agg"];
  onAggChange?: (agg: CustomExportMeasure["agg"]) => void;
  onRemove?: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `chip:${zone}:${data.key}`,
    data,
  });
  const { setNodeRef: setSlotRef } = useDroppable({
    id: `slot:${zone}:${index}`,
    data: { zone, index },
  });

  return (
    <span
      ref={(node) => {
        setNodeRef(node);
        setSlotRef(node);
      }}
      className={cn(CHIP, isDragging && "opacity-40")}
    >
      {note && <span className="text-muted-foreground">{note}</span>}
      <button
        type="button"
        className={cn("flex items-center gap-1", DRAG_HANDLE)}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3 text-muted-foreground/50" />
        {agg && onAggChange ? null : (
          <span className="font-medium">{label}</span>
        )}
      </button>
      {agg && onAggChange && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded px-0.5 font-medium text-muted-foreground hover:text-foreground"
                aria-label={`Change aggregation for ${label}`}
                title="Change aggregation"
              >
                {AGG_LABELS[agg]}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {AGG_KEYS.map((key) => (
                <DropdownMenuItem key={key} onClick={() => onAggChange(key)}>
                  {AGG_LABELS[key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="font-medium">{label}</span>
        </>
      )}
      {onRemove ? (
        <button
          type="button"
          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <X className="size-3" />
        </button>
      ) : (
        <span className="w-1" />
      )}
    </span>
  );
}

function ConditionChip({
  column,
  options,
  token,
  open,
  onOpenChange,
  values,
  onValuesChange,
  onApply,
  onRemove,
}: {
  column: string;
  options: Array<{ value: string; label: string }>;
  token?: CustomExportFilter;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: string[];
  onValuesChange: (values: string[]) => void;
  onApply: (negate: boolean) => void;
  onRemove: () => void;
}) {
  const summary = token
    ? token.values
        .map(
          (value) =>
            options.find((o) => o.value === value)?.label ??
            (value || "(blank)"),
        )
        .slice(0, 2)
        .join(", ") +
      (token.values.length > 2 ? ` +${token.values.length - 2}` : "")
    : "pick values";

  return (
    <span className={cn(CHIP, !token && "border-dashed")}>
      <Popover modal={false} open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1 text-left">
            <span className="font-medium">{column}</span>
            <span className="text-muted-foreground">
              {token?.negate ? "is none of" : "is any of"}
            </span>
            <span className="truncate">{summary}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandInput placeholder={`Search ${column.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const selected = values.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value || "(blank)"}
                      value={option.label}
                      onSelect={() =>
                        onValuesChange(
                          selected
                            ? values.filter((v) => v !== option.value)
                            : [...values, option.value],
                        )
                      }
                    >
                      <div
                        className={cn(
                          "mr-2 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                          selected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <Check className="size-3" />
                      </div>
                      <span className="min-w-0 flex-1">{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex items-center justify-end gap-1.5 border-t border-border/60 p-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={values.length === 0}
              onClick={() => onApply(true)}
              title="Exclude rows matching these values"
            >
              Apply NOT
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={values.length === 0}
              onClick={() => onApply(false)}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className="rounded p-0.5 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        aria-label={`Remove the ${column} filter`}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
